import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Chip,
  Fab,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Groups,
  Search,
  Add,
  Star,
  StarBorder,
  ArrowForward,
  SportsSoccer,
  SportsHockey,
  SportsBasketball,
  SportsTennis,
  SportsVolleyball,
} from '@mui/icons-material';
import { useTeams } from '@/hooks/useApi';
import { useAdminMode } from '@/hooks/useManage';
import { useFavoritesStore } from '@/store';
import { SportFilter, LoadingState, ErrorState } from '@/components';
import { BOTTOM_NAV_OFFSET } from '@/components/BottomNav';
import type { SportType, Team } from '@/types';

const getSportIcon = (sportType: SportType) => {
  switch (sportType) {
    case 'football':
      return <SportsSoccer />;
    case 'hockey':
      return <SportsHockey />;
    case 'basketball':
      return <SportsBasketball />;
    case 'tennis':
      return <SportsTennis />;
    case 'volleyball':
      return <SportsVolleyball />;
    default:
      return <SportsSoccer />;
  }
};

const getSportColor = (sportType: SportType): string => {
  const colors: Record<SportType, string> = {
    football: '#4caf50',
    hockey: '#2196f3',
    basketball: '#ff9800',
    tennis: '#e91e63',
    volleyball: '#9c27b0',
    handball: '#00bcd4',
    padel: '#8bc34a',
    swimming: '#03a9f4',
    poker: '#ff5722',
    other: '#757575',
  };
  return colors[sportType] || '#757575';
};

interface TeamListItemProps {
  team: Team;
  onClick: () => void;
}

const TeamListItem: React.FC<TeamListItemProps> = ({ team, onClick }) => {
  const { isTeamFavorite, addTeam, removeTeam } = useFavoritesStore();
  const isFavorite = isTeamFavorite(team.id);

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isFavorite) {
      removeTeam(team.id);
    } else {
      addTeam(team.id);
    }
  };

  return (
    <ListItem
      onClick={onClick}
      sx={{
        bgcolor: 'background.paper',
        mb: 0.5,
        borderRadius: 1,
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
      }}
      secondaryAction={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            edge="end"
            onClick={handleFavoriteClick}
            sx={{ color: isFavorite ? 'warning.main' : 'text.secondary' }}
          >
            {isFavorite ? <Star /> : <StarBorder />}
          </IconButton>
          <ArrowForward color="action" />
        </Box>
      }
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: getSportColor(team.sportType) }}>
          {getSportIcon(team.sportType)}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography variant="body1" fontWeight="medium">
            {team.name}
          </Typography>
        }
        secondary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Chip
              label={team.sportType}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                textTransform: 'capitalize',
              }}
            />
            {team.cityName && <Chip label={team.cityName} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />}
            {team.roster && (
              <Typography variant="caption" color="text.secondary">
                {team.roster.length} игроков
              </Typography>
            )}
          </Box>
        }
      />
    </ListItem>
  );
};

export const TeamsPage: React.FC = () => {
  const navigate = useNavigate();
  const { create } = useAdminMode();
  const [selectedSport, setSelectedSport] = useState<SportType | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const [searchQuery, setSearchQuery] = useState(q);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const value = searchQuery.trim();
      if (value === q) return;
      const next = new URLSearchParams(searchParams);
      if (value) next.set('q', value);
      else next.delete('q');
      setSearchParams(next, { replace: true });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchQuery, q, searchParams, setSearchParams]);

  const {
    data: teams,
    isLoading,
    error,
    refetch,
  } = useTeams({ limit: 50, sportType: selectedSport || undefined, q: q || undefined });

  const filteredTeams = teams?.data.filter((team) => team.kind !== 'pickup');

  return (
    <Box sx={{ pb: 10 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Groups color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h5" fontWeight="bold">
            Команды
          </Typography>
        </Box>
      </Box>

      {/* Search */}
      <Box sx={{ px: 2, pb: 2 }}>
        <TextField
          fullWidth
          placeholder="Поиск команд..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
          }}
          size="small"
        />
      </Box>

      {/* Sport Filter */}
      <SportFilter selectedSport={selectedSport} onSelectSport={setSelectedSport} />

      {/* Teams List */}
      <Box sx={{ px: 1, pt: 2 }}>
        {isLoading ? (
          <LoadingState message="Загрузка команд..." />
        ) : error ? (
          <ErrorState message="Ошибка загрузки команд" onRetry={refetch} />
        ) : filteredTeams?.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Groups sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Нет команд
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {searchQuery
                ? 'По вашему запросу ничего не найдено'
                : 'Создайте первую команду'}
            </Typography>
          </Paper>
        ) : (
          <List sx={{ p: 0 }}>
            {filteredTeams?.map((team) => (
              <TeamListItem
                key={team.id}
                team={team}
                onClick={() => navigate(`/teams/${team.id}`)}
              />
            ))}
          </List>
        )}
      </Box>

      {create.team && (
        <Fab
          color="manage"
          aria-label="Создать команду"
          sx={{ position: 'fixed', bottom: { xs: `calc(${BOTTOM_NAV_OFFSET} + 16px)`, lg: 24 }, right: 16 }}
          onClick={() => navigate('/teams/new')}
        >
          <Add />
        </Fab>
      )}
    </Box>
  );
};
